import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MessageSquare,
  Clock,
  User,
  Calendar,
  ExternalLink,
  Download,
  Share2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Target,
  Zap,
  XCircle,
  Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { getPiiData, type PiiData, createShareableChatUrl } from '@/utils/piiUtils';

interface Message {
  id: string;
  content: string;
  type: string;
  created_at: string;
  question_id?: string;
  metadata?: any;
}

interface UserResponse {
  id: string;
  question_id: string;
  response_text?: string;
  response_options?: string[];
  created_at: string;
  questions?: {
    id: string;
    text: string;
    shortname?: string;
  };
}

interface TriggerLog {
  id: string;
  trigger_rule_id: string;
  conversation_id: string;
  payload: any;
  response?: any;
  status: string;
  error_message?: string;
  executed_at: string;
  execution_time_ms?: number;
  response_status?: number;
  response_data?: any;
  chatbot_id?: string;
  session_id?: string;
  trigger_name?: string;
  webhook_url?: string;
  timestamp?: string;
  pii_data?: any;
  trigger_rules?: {
    id: string;
    name: string;
    webhookurl?: string;
  };
}

interface ConversationDetail {
  id: string;
  chatbot_id: string;
  session_id?: string;
  status: string;
  metadata: any;
  created_at: string;
  updated_at: string;
  messages: Message[];
  user_responses: UserResponse[];
  trigger_logs: TriggerLog[];
  chatbot?: {
    id: string;
    settings?: any;
  };
}

interface ConversationDetailContentProps {
  conversationId: string | null;
  onExternalActions?: {
    onViewFullPage?: () => void;
    onRefresh?: () => void;
    onShare?: () => void;
    onExport?: () => void;
  };
  showExternalActions?: boolean;
}

export const ConversationDetailContent: React.FC<ConversationDetailContentProps> = ({
  conversationId,
  onExternalActions,
  showExternalActions = true
}) => {
  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [piiData, setPiiData] = useState<PiiData | null>(null);
  const [piiToken, setPiiToken] = useState<string | null>(null);
  const [isPiiLoading, setIsPiiLoading] = useState(false);
  const { toast } = useToast();

  const fetchPiiData = async (token: string) => {
    if (!token) return;

    setIsPiiLoading(true);
    try {
      const data = await getPiiData(token);
      setPiiData(data);
    } catch (err) {
      console.warn('Failed to fetch PII data:', err);
      setPiiData(null);
    } finally {
      setIsPiiLoading(false);
    }
  };

  const fetchConversationDetail = async () => {
    if (!conversationId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch conversation with messages and user responses
      const { data: conversationData, error: convError } = await supabase
        .from('conversations')
        .select(`
          *,
          chatbot:chatbot_id (
            id,
            settings
          )
        `)
        .eq('id', conversationId)
        .single();

      if (convError) throw convError;

      // Fetch messages for this conversation
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      // Fetch user responses for this conversation with question details
      const { data: responsesData, error: responsesError } = await supabase
        .from('user_responses')
        .select(`
          *,
          questions (
            id,
            text,
            shortname
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (responsesError) throw responsesError;

      // Fetch trigger logs for this conversation
      const { data: triggerLogsData, error: triggerLogsError } = await supabase
        .from('trigger_logs')
        .select(`
          *,
          trigger_rules (
            id,
            name,
            webhookurl
          )
        `)
        .eq('conversation_id', conversationId)
        .order('executed_at', { ascending: true });

      if (triggerLogsError) throw triggerLogsError;

      const detailedConversation: ConversationDetail = {
        ...conversationData,
        messages: messagesData || [],
        user_responses: responsesData || [],
        trigger_logs: triggerLogsData || []
      };

      setConversation(detailedConversation);

      // Check for PII token in conversation metadata
      if (conversationData.metadata?.pii_token) {
        setPiiToken(conversationData.metadata.pii_token);
        await fetchPiiData(conversationData.metadata.pii_token);
      } else {
        // Also check for session-based PII tokens
        const sessionId = conversationData.session_id;
        if (sessionId) {
          try {
            // Try to get auto-created PII token from session storage pattern
            const autoToken = sessionStorage.getItem(`pii_token_${conversationData.chatbot_id}_${sessionId}`);
            if (autoToken) {
              setPiiToken(autoToken);
              await fetchPiiData(autoToken);
            } else {
              // If no session storage token, try to find PII data by searching for session metadata
              const { data: piiTokensData, error: piiError } = await supabase
                .from('pii_tokens')
                .select(`
                  token,
                  pii_data!inner(
                    data
                  )
                `)
                .contains('pii_data.data', { _session_id: sessionId })
                .order('created_at', { ascending: false })
                .limit(1);

              if (!piiError && piiTokensData && piiTokensData.length > 0) {
                const foundToken = piiTokensData[0].token;
                setPiiToken(foundToken);
                await fetchPiiData(foundToken);
              } else {
                // Clear PII data if no token found
                setPiiData(null);
                setPiiToken(null);
              }
            }
          } catch (error) {
            console.warn('Failed to find auto-created PII token:', error);
            setPiiData(null);
            setPiiToken(null);
          }
        } else {
          // Clear PII data if no session ID
          setPiiData(null);
          setPiiToken(null);
        }
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch conversation details';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (conversationId) {
      fetchConversationDetail();
    }
  }, [conversationId]);

  const exportConversation = () => {
    if (!conversation) return;

    const exportData = {
      conversation_id: conversation.id,
      status: conversation.status,
      created_at: conversation.created_at,
      metadata: conversation.metadata,
      messages: conversation.messages.map(msg => ({
        content: msg.content,
        sender: msg.type === 'bot' || msg.type === 'system' || msg.type === 'ai' ? 'bot' : 'user',
        timestamp: msg.created_at,
        type: msg.type
      })),
      user_responses: conversation.user_responses.map(resp => ({
        question_id: resp.question_id,
        question_text: resp.questions?.text || 'Question not found',
        question_shortname: resp.questions?.shortname || null,
        response_text: resp.response_text,
        response_options: resp.response_options,
        timestamp: resp.created_at
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${conversation.id}-${format(new Date(), 'yyyy-MM-dd')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const shareConversation = async () => {
    if (!conversation || !piiToken) {
      toast({
        title: "Cannot share conversation",
        description: "This conversation doesn't have shareable data or PII token.",
        variant: "destructive",
      });
      return;
    }

    try {
      const shareableUrl = createShareableChatUrl(conversation.id, piiToken);
      await navigator.clipboard.writeText(shareableUrl);

      toast({
        title: "Shareable link copied!",
        description: "The conversation link has been copied to your clipboard. Users can continue this chat with all data intact.",
      });
    } catch (error) {
      toast({
        title: "Failed to copy link",
        description: "Please copy the link manually from the PII section below.",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = () => {
    const isCompleted = conversation?.metadata?.completed_at || conversation?.metadata?.completed;

    if (isCompleted) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
        <AlertCircle className="h-3 w-3 mr-1" />
        In Progress
      </Badge>
    );
  };

  const formatMetadata = (metadata: any) => {
    if (!metadata) return {};

    return {
      'UTM Source': metadata.utm_source || 'Direct',
      'UTM Medium': metadata.utm_medium || 'N/A',
      'UTM Campaign': metadata.utm_campaign || 'N/A',
      'Referrer': metadata.referrer || 'Direct',
      'User Agent': metadata.userAgent ?
        metadata.userAgent.split(' ').slice(0, 3).join(' ') + '...' : 'N/A',
      'Starting URL': metadata.startingUrl || 'N/A'
    };
  };

  const handleRefresh = () => {
    if (onExternalActions?.onRefresh) {
      onExternalActions.onRefresh();
    } else {
      fetchConversationDetail();
    }
  };

  const handleExport = () => {
    if (onExternalActions?.onExport) {
      onExternalActions.onExport();
    } else {
      exportConversation();
    }
  };

  const handleShare = () => {
    if (onExternalActions?.onShare) {
      onExternalActions.onShare();
    } else {
      shareConversation();
    }
  };

  if (!conversationId) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Conversation Selected</h3>
        <p className="text-gray-600">Please select a conversation to view details.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Conversation Details</h2>
          {conversation && getStatusBadge()}
        </div>
        {showExternalActions && (
          <div className="flex items-center gap-2">
            {onExternalActions?.onViewFullPage && (
              <Button
                variant="outline"
                size="sm"
                onClick={onExternalActions.onViewFullPage}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Full Page
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {piiToken && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                disabled={!conversation || !piiToken}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={!conversation}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          <span>Loading conversation details...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-8 text-red-600">
          <AlertCircle className="h-6 w-6 mx-auto mb-2" />
          <p>Error: {error}</p>
        </div>
      )}

      {/* Content */}
      {conversation && !isLoading && (
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <Tabs defaultValue="timeline" className="w-full">
                  <TabsList className={`grid w-full ${(() => {
                    const settings = typeof conversation.chatbot?.settings === 'string' 
                      ? JSON.parse(conversation.chatbot.settings) 
                      : conversation.chatbot?.settings;
                    return settings?.chatMode !== 'ai_only';
                  })() ? 'grid-cols-4' : 'grid-cols-3'}`}>
                    <TabsTrigger value="timeline">Timeline</TabsTrigger>
                    <TabsTrigger value="messages">Messages</TabsTrigger>
                    {(() => {
                      const settings = typeof conversation.chatbot?.settings === 'string' 
                        ? JSON.parse(conversation.chatbot.settings) 
                        : conversation.chatbot?.settings;
                      return settings?.chatMode !== 'ai_only';
                    })() && (
                      <TabsTrigger value="responses">Responses</TabsTrigger>
                    )}
                    <TabsTrigger value="triggers">Triggers</TabsTrigger>
                  </TabsList>

                  <TabsContent value="timeline" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          <span>Conversation Timeline</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-96 pr-4">
                          <div className="space-y-4">
                            {/* Messages and responses combined in chronological order */}
                            {[
                              ...conversation.messages.map(msg => ({ ...msg, type: 'message', timestamp: msg.created_at })),
                              ...(() => {
                                const settings = typeof conversation.chatbot?.settings === 'string' 
                                  ? JSON.parse(conversation.chatbot.settings) 
                                  : conversation.chatbot?.settings;
                                return settings?.chatMode !== 'ai_only';
                              })() 
                                ? conversation.user_responses.map(resp => ({ ...resp, type: 'response', timestamp: resp.created_at }))
                                : []
                            ]
                              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                              .map((item, index) => (
                                <div key={`${item.type}-${item.id}`} className="flex gap-3">
                                  <div className="flex-shrink-0">
                                    {item.type === 'message' ? (
                                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <MessageSquare className="h-4 w-4 text-blue-600" />
                                      </div>
                                    ) : (
                                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                        <User className="h-4 w-4 text-green-600" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-gray-500">
                                      {format(new Date(item.timestamp), 'MMM d, yyyy h:mm:ss a')}
                                    </div>
                                    <div className="mt-1">
                                      {item.type === 'message' ? (
                                        <div className="bg-blue-50 rounded-lg p-3">
                                          <p className="text-sm">{(item as Message).content}</p>
                                        </div>
                                      ) : (
                                        <div className="bg-green-50 rounded-lg p-3">
                                          <p className="text-sm">
                                            <strong>User Response:</strong> {(item as UserResponse).response_text}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="messages" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Bot Messages ({conversation.messages.length})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-96">
                          <div className="space-y-3">
                            {conversation.messages.map((message) => (
                              <div key={message.id} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <Badge variant="secondary">{message.type}</Badge>
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(message.created_at), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                <p className="text-sm">{message.content}</p>
                                {message.metadata && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    <strong>Metadata:</strong> {JSON.stringify(message.metadata)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {(() => {
                    const settings = typeof conversation.chatbot?.settings === 'string' 
                      ? JSON.parse(conversation.chatbot.settings) 
                      : conversation.chatbot?.settings;
                    return settings?.chatMode !== 'ai_only';
                  })() && (
                    <TabsContent value="responses" className="mt-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>User Responses ({conversation.user_responses.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-96">
                            <div className="space-y-3">
                              {conversation.user_responses.map((response) => (
                                <div key={response.id} className="border rounded-lg p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="text-sm font-medium">
                                      {response.questions?.shortname || 'Response'}
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {format(new Date(response.created_at), 'MMM d, h:mm a')}
                                    </span>
                                  </div>
                                  <p className="text-sm">{response.response_text}</p>
                                  {response.questions?.text && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      <strong>Question:</strong> {response.questions.text}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  )}

                  <TabsContent value="triggers" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Trigger Logs ({conversation.trigger_logs.length})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-96">
                          <div className="space-y-3">
                            {conversation.trigger_logs.map((trigger) => (
                              <div key={trigger.id} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge 
                                      variant={trigger.status === 'success' ? 'default' : 'destructive'}
                                    >
                                      {trigger.status}
                                    </Badge>
                                    <span className="text-sm font-medium">
                                      {trigger.trigger_rules?.name || 'Unknown Trigger'}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(trigger.executed_at), 'MMM d, h:mm a')}
                                  </span>
                                </div>
                                {trigger.webhook_url && (
                                  <p className="text-xs text-gray-600 mb-2">
                                    <strong>URL:</strong> {trigger.webhook_url}
                                  </p>
                                )}
                                {trigger.execution_time_ms && (
                                  <p className="text-xs text-gray-600 mb-2">
                                    <strong>Execution Time:</strong> {trigger.execution_time_ms}ms
                                  </p>
                                )}
                                {trigger.error_message && (
                                  <p className="text-xs text-red-600">
                                    <strong>Error:</strong> {trigger.error_message}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Status Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      <span>Status</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      {getStatusBadge()}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Chatbot ID</span>
                      <span className="text-sm font-mono text-right">{conversation.chatbot_id}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Session ID</span>
                      <span className="text-sm font-mono text-right">{conversation.session_id || 'N/A'}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Created</span>
                      <span className="text-sm text-right">
                        {format(new Date(conversation.created_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Updated</span>
                      <span className="text-sm text-right">
                        {format(new Date(conversation.updated_at), 'MMM d, yyyy h:mm a')}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Statistics Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Messages</span>
                      <span className="text-sm font-semibold">{conversation.messages.length}</span>
                    </div>
                    {(() => {
                      const settings = typeof conversation.chatbot?.settings === 'string' 
                        ? JSON.parse(conversation.chatbot.settings) 
                        : conversation.chatbot?.settings;
                      return settings?.chatMode !== 'ai_only';
                    })() && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">User Responses</span>
                        <span className="text-sm font-semibold">{conversation.user_responses.length}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Triggers</span>
                      <span className="text-sm font-semibold">{conversation.trigger_logs.length}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Metadata Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Metadata</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(formatMetadata(conversation.metadata)).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{key}</span>
                          <span className="text-sm font-semibold text-right max-w-32 truncate" title={String(value)}>
                            {String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* PII Data Card */}
                {piiData && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        <span>Collected Data</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(piiData).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</span>
                            <span className="text-sm font-semibold text-right max-w-32 truncate" title={String(value)}>
                              {String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* AI Extracted Data Card */}
                {conversation?.metadata?.ai_extracted_data && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Zap className="h-5 w-5" />
                        <span>AI Extracted Data</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(conversation.metadata.ai_extracted_data).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between">
                            <span className="text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</span>
                            <span className="text-sm font-semibold text-right max-w-32 truncate" title={String(value)}>
                              {String(value)}
                            </span>
                          </div>
                        ))}
                        {conversation.metadata.extracted_at && (
                          <div className="pt-2 mt-2 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Extracted</span>
                              <span className="text-xs text-gray-500">
                                {format(new Date(conversation.metadata.extracted_at), 'MMM d, h:mm a')}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
